/**
 * The Repository access page's state and actions: which dialog is open, what the test panel holds, and the
 * multi-step operations (save-then-place a rule, save-then-test a token). The page only lays this out.
 */
import { useRef, useState } from 'react'
import { toast } from 'sonner'
import {
  useDeleteGitRoutingRule,
  useDeploymentGitRouting,
  useGitCredentials,
  useGitRoutingRules,
  useRemoveGitCredential,
  useReorderGitRoutingRules,
  useSetGitCredential,
  useTestGitRoutingUrl,
  useUpsertGitRoutingRule,
} from '@/api/gitRouting'
import { usePermissions } from '@/hooks/usePermissions'
import { credentialUsage, hostDefaultCredential, isCredentialTemplate, patternMatches, patternStem, placeAt } from '@/lib/gitRouting'
import type { RoutingRuleDraft } from '@/components/repository-access/RoutingRuleDialog'
import type { GitCredential, GitRoutingRule, UpsertGitRoutingRuleRequest } from '@/types/gitRouting'

interface RuleDialogState {
  open: boolean
  rule?: GitRoutingRule
  draft?: RoutingRuleDraft
}

interface TokenDialogState {
  open: boolean
  name?: string
  replacing: boolean
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : 'Something went wrong.'
}

export function useRepositoryAccess() {
  const { hasPermission } = usePermissions()
  const canEdit = hasPermission('config:edit')
  const canManageTokens = hasPermission('credentials:manage')

  const rulesQuery = useGitRoutingRules()
  const deploymentQuery = useDeploymentGitRouting()
  const credentialsQuery = useGitCredentials()
  const upsert = useUpsertGitRoutingRule()
  const remove = useDeleteGitRoutingRule()
  const reorder = useReorderGitRoutingRules()
  const setToken = useSetGitCredential()
  const removeToken = useRemoveGitCredential()
  const test = useTestGitRoutingUrl()

  const rules = [...(rulesQuery.data?.rules ?? [])].sort((a, b) => a.position - b.position)
  const deployment = deploymentQuery.data
  const credentialsAvailable = credentialsQuery.data?.available ?? true
  const credentialsUnavailable = credentialsQuery.isError
  const credentials = credentialsQuery.data?.credentials ?? []
  /** What the store said, or undefined when it could not say — which reads as Unknown, never Missing. */
  const knownCredentials = credentialsQuery.data?.available && !credentialsQuery.isError ? credentials : undefined

  const credentialNames = [
    ...new Set(
      [
        ...credentials.map((c) => c.name),
        ...rules.map((r) => r.credentialName),
        ...(deployment?.rules ?? []).map((r) => r.credentialRef ?? hostDefaultCredential(r.provider)),
        ...(deployment?.hostDefaults ?? []).map((h) => hostDefaultCredential(h.provider)),
      ].filter((n) => !isCredentialTemplate(n)),
    ),
  ].sort()

  const [ruleDialog, setRuleDialog] = useState<RuleDialogState>({ open: false })
  const [tokenDialog, setTokenDialog] = useState<TokenDialogState>({ open: false, replacing: false })
  const [deletingRule, setDeletingRule] = useState<GitRoutingRule>()
  const [removingCredential, setRemovingCredential] = useState<GitCredential>()
  const [testUrl, setTestUrl] = useState('')
  const testInputRef = useRef<HTMLInputElement>(null)

  const runTest = (url: string) => {
    setTestUrl(url)
    test.mutate(url)
  }

  /** Puts a URL in the test box and hands it to the user to finish, or runs it when it is complete. */
  const startTest = (url: string) => {
    if (url.includes('*') || url.endsWith('/')) {
      setTestUrl(url)
      test.reset()
      testInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      testInputRef.current?.focus()
    } else {
      runTest(url)
    }
  }

  const saveRule = async (request: UpsertGitRoutingRuleRequest, position: number) => {
    const saved = await upsert.mutateAsync(request)
    if (saved.position !== position) {
      await reorder.mutateAsync(placeAt(rules.map((r) => r.id), saved.id, position))
    }
    toast.success(request.id ? `Rule ${position} saved` : `Rule added at position ${position}`)
  }

  const saveToken = async (name: string, value: string, andTest: boolean) => {
    await setToken.mutateAsync({ name, value })
    toast.success(`Token for ${name} saved`)

    if (andTest) {
      // The URL already in the box, when it is one this credential serves; otherwise the stem of the first
      // rule that uses it, for the user to complete.
      const usedBy = credentialUsage(name, rules, deployment).rules
      if (testUrl && usedBy.some((r) => patternMatches(r.urlPattern, testUrl))) {
        runTest(testUrl)
      } else if (usedBy.length > 0) {
        startTest(patternStem(usedBy[0].urlPattern))
        toast.info(`Complete the URL of a repository ${name} should reach, then press Test.`)
      } else {
        startTest('')
        toast.info(`Paste the URL of a repository ${name} should reach, then press Test.`)
      }
    }
  }

  const confirmDeleteRule = async () => {
    if (!deletingRule) return
    try {
      await remove.mutateAsync(deletingRule.id)
      toast.success(`Rule ${deletingRule.position} deleted`)
    } catch (error) {
      toast.error(messageOf(error))
    } finally {
      setDeletingRule(undefined)
    }
  }

  const confirmRemoveCredential = async () => {
    if (!removingCredential) return
    try {
      await removeToken.mutateAsync(removingCredential.name)
      toast.success(`Token for ${removingCredential.name} removed`)
    } catch (error) {
      toast.error(messageOf(error))
    } finally {
      setRemovingCredential(undefined)
    }
  }

  const reorderRules = (ids: string[]) =>
    reorder.mutate(ids, { onError: (error) => toast.error(messageOf(error)) })

  return {
    canEdit,
    canManageTokens,
    rules,
    rulesLoading: rulesQuery.isLoading,
    rulesError: rulesQuery.error,
    deployment,
    credentials,
    knownCredentials,
    credentialsLoading: credentialsQuery.isLoading,
    credentialsAvailable,
    credentialsUnavailable,
    credentialNames,

    ruleDialog,
    openAddRule: (draft?: RoutingRuleDraft) => setRuleDialog({ open: true, draft }),
    openEditRule: (rule: GitRoutingRule) => setRuleDialog({ open: true, rule }),
    setRuleDialogOpen: (open: boolean) => setRuleDialog((s) => ({ ...s, open })),
    saveRule,

    tokenDialog,
    tokenUsedBy: tokenDialog.name ? credentialUsage(tokenDialog.name, rules, deployment).rules : [],
    openSetToken: (name?: string, replacing?: boolean) =>
      setTokenDialog({
        open: true,
        name,
        replacing: replacing ?? credentials.some((c) => c.name.toLowerCase() === name?.toLowerCase()),
      }),
    setTokenDialogOpen: (open: boolean) => setTokenDialog((s) => ({ ...s, open })),
    saveToken,

    deletingRule,
    setDeletingRule,
    confirmDeleteRule,
    isDeletingRule: remove.isPending,

    removingCredential,
    removingCredentialUsedBy: removingCredential
      ? credentialUsage(removingCredential.name, rules, deployment).rules
      : [],
    setRemovingCredential,
    confirmRemoveCredential,
    isRemovingCredential: removeToken.isPending,

    reorderRules,
    isReordering: reorder.isPending,

    testUrl,
    setTestUrl,
    testInputRef,
    runTest,
    testWithRule: (rule: GitRoutingRule) => startTest(patternStem(rule.urlPattern)),
    testResult: test.data,
    isTesting: test.isPending,
    testError: test.error ? messageOf(test.error) : undefined,
  }
}
